// Global Instructions Rule Applied!
//
// Account form — Legal Rights tab (Phase 5, signed-off Mockups 5.1/5.2 with
// owner's layout refinement): a "Rights In Force" summary first — every
// currently in-force / at-risk right across all agreement sets, tagged with
// the set/round it comes from — then the per-set breakdown (draft / executed
// / superseded, nothing hard-deleted). Statuses come from the live evaluator
// (legal__evaluate_rights) against the CURRENT cap-table position; clicking
// a status shows the arithmetic. Capture is MANUAL only (editor modal);
// Execute promotes a draft with an explicit supersession picker, Revert
// symmetrically un-supersedes.
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { Alert, Button, Checkbox, Collapse, Modal, Popconfirm, Popover, Spin, Table, Tag } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  LegalAgreementSet,
  LegalReferenceArithmetic,
  LegalRight,
  LegalRightsData,
  LegalRuleArithmetic
} from '../../../state_manager/data_models/legal_rights_model.ts'
import {
  LegalSetSavePayload,
  deleteLegalSet,
  fetchLegalRights,
  setLegalSetStatus
} from '../services/dealExecutionService.ts'
import LegalSetEditorModal, { LegalReferenceOption } from './LegalSetEditorModal.tsx'
import { pct, shares } from './CapTableTab.tsx'

export const SET_STATUS_COLORS: Record<string, string> = {
  draft: 'blue',
  executed: 'green',
  superseded: 'default'
}

const RIGHT_STATUS_META: Record<string, { color: string; label: string }> = {
  in_force: { color: 'green', label: 'In force' },
  at_risk: { color: 'gold', label: 'At risk' },
  lapsed: { color: 'default', label: 'Lapsed' },
  pending: { color: 'blue', label: 'Pending' },
  unknown: { color: 'default', label: 'Unknown' }
}

const STATUS_REASON_LABELS: Record<string, string> = {
  superseded: 'superseded',
  condition_not_met: 'condition not met',
  ended: 'ended',
  set_draft: 'set is draft',
  not_yet_effective: 'not yet effective',
  no_cap_table: 'no cap table',
  unsupported_variable: 'variable not evaluated',
  near_threshold: 'near threshold',
  reference_untracked: 'reference untracked'
}

const OPERATOR_LABELS: Record<string, string> = {
  '>=': '≥',
  '>': '>',
  '<=': '≤',
  '<': '<',
  '=': '='
}

function formatRuleValue(value: number | null | undefined, unit: string | null | undefined): string {
  if (value == null) return '—'
  return unit === 'percent' ? pct(value) : shares(value)
}

export function ruleText(right: LegalRight): string | null {
  if (right.legal_rights__basis === 'rule') {
    const arith = right.evaluation.arithmetic as LegalRuleArithmetic | null | undefined
    const label = arith?.variable_label || right.legal_rights__rule_variable || 'value'
    const op = OPERATOR_LABELS[right.legal_rights__rule_operator || '>='] || '≥'
    const threshold = formatRuleValue(right.legal_rights__rule_threshold, arith?.unit)
    return `${label} ${op} ${threshold}`
  }
  if (right.legal_rights__basis === 'right_reference') {
    return `References ${right.referenced_right_name || right.legal_rights__reference_text || 'an untracked term'}`
  }
  return null
}

// Status tag with the evaluation arithmetic behind a click (Popover).
export function RightStatusTag({ right }: { right: LegalRight }): React.ReactElement {
  const ev = right.evaluation
  const meta = RIGHT_STATUS_META[ev.status || 'unknown'] || RIGHT_STATUS_META.unknown
  const reason = ev.reason && ev.reason !== 'near_threshold' ? STATUS_REASON_LABELS[ev.reason] || ev.reason : null
  const tag = (
    <Tag color={meta.color} className='cursor-pointer'>
      {meta.label}
      {reason ? ` — ${reason}` : ''}
    </Tag>
  )
  const refArith = ev.arithmetic as LegalReferenceArithmetic | null | undefined
  const arith: LegalRuleArithmetic | null | undefined =
    refArith && 'referenced' in (refArith as object)
      ? refArith.referenced
      : (ev.arithmetic as LegalRuleArithmetic | null | undefined)
  if (!arith || arith.value == null) return tag
  const rows: Array<[string, string]> = [
    [
      'Rule',
      `${arith.variable_label || arith.variable || 'value'} ${OPERATOR_LABELS[arith.operator || '>='] || '≥'} ${formatRuleValue(arith.threshold, arith.unit)}`
    ],
    ['Our units (equity lines)', shares(arith.our_units)],
    ['Fully-diluted total (equity)', shares(arith.fd_total)],
    ['Value', formatRuleValue(arith.value, arith.unit)],
    ['At-risk margin', arith.margin != null ? pct(Number(arith.margin) * 100) : '—']
  ]
  return (
    <Popover
      trigger='click'
      title={`Evaluation — ${right.legal_rights__name}`}
      content={
        <div className='text-xs space-y-1 min-w-[260px]'>
          {refArith && 'referenced_right_name' in (refArith as object) && refArith.referenced_right_name && (
            <div className='text-gray-500'>via {refArith.referenced_right_name}</div>
          )}
          {rows.map(([label, value]) => (
            <div key={label} className='flex justify-between gap-6'>
              <span className='text-gray-500'>{label}</span>
              <span className='font-medium tabular-nums'>{value}</span>
            </div>
          ))}
          {arith.as_of_date && (
            <div className='text-gray-400 pt-1'>
              Cap table as of {dayjs(arith.as_of_date).format('MM/DD/YYYY')}
            </div>
          )}
        </div>
      }
    >
      {tag}
    </Popover>
  )
}

function SourceCell({ right }: { right: LegalRight }): React.ReactElement {
  const parts: React.ReactNode[] = []
  if (right.file_urls_deals__file_name) {
    parts.push(
      <span key='file' className='text-gray-700'>
        {right.file_urls_deals__file_name}
      </span>
    )
  } else if (right.legal_rights__source_url) {
    parts.push(
      <a
        key='url'
        className='cursor-pointer text-blue-600 hover:underline'
        href={right.legal_rights__source_url}
        target='_blank'
        rel='noreferrer'
      >
        Linked document
      </a>
    )
  }
  if (right.legal_rights__section_ref) {
    parts.push(
      <span key='ref' className='text-gray-500'>
        {parts.length ? ' · ' : ''}
        {right.legal_rights__section_ref}
      </span>
    )
  }
  if (!parts.length) return <span className='text-gray-400'>—</span>
  return <span className='text-xs'>{parts}</span>
}

interface LegalRightsTabProps {
  accountId: string | null
  // Lets the host form's Refresh button reach this tab's own data fetch
  // (HoldingsTab convention on the company full form).
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
}

export default function LegalRightsTab({
  accountId,
  onRefreshReady
}: LegalRightsTabProps): React.ReactElement {
  const [data, setData] = useState<LegalRightsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorInitial, setEditorInitial] = useState<LegalSetSavePayload | null>(null)
  // Set history is collapsed by default (owner refinement); per-set expansion
  // plus an Expand All / Collapse All control.
  const [expandedSets, setExpandedSets] = useState<string[]>([])
  const [executeSet, setExecuteSet] = useState<LegalAgreementSet | null>(null)
  const [executeDate, setExecuteDate] = useState<string | null>(null)
  const [executeSupersedes, setExecuteSupersedes] = useState<string[]>([])
  const [executeSaving, setExecuteSaving] = useState(false)

  const refetch = useCallback(async (): Promise<void> => {
    if (!accountId) return
    const token = getAccessToken(store.getState().auth)
    const result = await fetchLegalRights(token, accountId)
    if (result.error) showErrorToast(result.error)
    setData(result.data)
    setLoading(false)
  }, [accountId])

  useEffect(() => {
    setLoading(true)
    refetch()
  }, [refetch])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refetch)
  }, [onRefreshReady, refetch])

  // Rights and rounds change from deal forms while this tab stays mounted —
  // refresh when the user comes back to it.
  const activateRef = useRefreshOnActivate(refetch)

  // All rights currently in force / at risk, tagged with their set.
  const inForceRows = useMemo(() => {
    if (!data) return []
    return data.sets.flatMap((s) =>
      s.rights
        .filter((r) => ['in_force', 'at_risk'].includes(r.evaluation.status || ''))
        .map((r) => ({ set: s, right: r }))
    )
  }, [data])

  const referenceOptions: LegalReferenceOption[] = useMemo(() => {
    if (!data) return []
    return data.sets.flatMap((s) =>
      s.rights.map((r) => ({
        value: r.legal_rights__id,
        label: `${r.legal_rights__name} — ${s.legal_agreement_sets__name}`
      }))
    )
  }, [data])

  if (!accountId) return <></>
  if (loading || !data) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  const position = data.position
  const allExpanded = data.sets.length > 0 && expandedSets.length === data.sets.length

  const openNewSet = (): void => {
    setEditorInitial({ account_id: accountId, name: '', rights: [] })
    setEditorOpen(true)
  }

  const openEditSet = (s: LegalAgreementSet): void => {
    // Expand the set being edited so the saved result is visible on close.
    setExpandedSets((prev) =>
      prev.includes(s.legal_agreement_sets__id) ? prev : [...prev, s.legal_agreement_sets__id]
    )
    setEditorInitial({
      id: s.legal_agreement_sets__id,
      account_id: accountId,
      round_id: s.rounds__id ?? null,
      deal_id: s.deals__id ?? null,
      name: s.legal_agreement_sets__name,
      executed_date: s.legal_agreement_sets__executed_date ?? null,
      notes: s.legal_agreement_sets__notes ?? null,
      rights: s.rights.map((r) => ({
        id: r.legal_rights__id,
        right_type_id: r.legal_right_types__id ?? null,
        name: r.legal_rights__name,
        basis: r.legal_rights__basis as 'absolute' | 'rule' | 'right_reference',
        rule_variable: r.legal_rights__rule_variable ?? null,
        rule_operator: r.legal_rights__rule_operator ?? '>=',
        rule_threshold: r.legal_rights__rule_threshold ?? null,
        referenced_right_id: r.legal_rights__referenced_right_id ?? null,
        reference_text: r.legal_rights__reference_text ?? null,
        carried: r.legal_rights__carried,
        section_ref: r.legal_rights__section_ref ?? null,
        source_file_id: r.file_urls_deals__id ?? null,
        source_url: r.legal_rights__source_url ?? null
      }))
    })
    setEditorOpen(true)
  }

  const openExecute = (s: LegalAgreementSet): void => {
    setExecuteSet(s)
    setExecuteDate(s.legal_agreement_sets__executed_date ?? dayjs().format('YYYY-MM-DD'))
    setExecuteSupersedes(
      data.sets
        .filter((x) => x.legal_agreement_sets__status === 'executed')
        .map((x) => x.legal_agreement_sets__id)
    )
  }

  const runExecute = async (): Promise<void> => {
    if (!executeSet) return
    setExecuteSaving(true)
    const token = getAccessToken(store.getState().auth)
    const result = await setLegalSetStatus(token, {
      setId: executeSet.legal_agreement_sets__id,
      action: 'execute',
      executedDate: executeDate,
      supersedes: executeSupersedes
    })
    setExecuteSaving(false)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to execute agreement set')
      return
    }
    toast.success('Agreement set executed')
    setExecuteSet(null)
    await refetch()
  }

  const runRevert = async (s: LegalAgreementSet): Promise<void> => {
    const token = getAccessToken(store.getState().auth)
    const result = await setLegalSetStatus(token, {
      setId: s.legal_agreement_sets__id,
      action: 'revert_to_draft'
    })
    if (!result.success) {
      showErrorToast(result.error || 'Failed to revert agreement set')
      return
    }
    toast.success('Reverted to draft')
    await refetch()
  }

  const runDelete = async (s: LegalAgreementSet): Promise<void> => {
    const token = getAccessToken(store.getState().auth)
    const result = await deleteLegalSet(token, s.legal_agreement_sets__id)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to delete agreement set')
      return
    }
    toast.success('Agreement set deleted')
    await refetch()
  }

  const rightColumns = (showCarriedNote: boolean) => [
    {
      title: 'Right',
      key: 'right',
      width: '26%',
      render: (_: unknown, r: LegalRight) => (
        <span>
          <span className='font-medium'>{r.legal_rights__name}</span>
          <div className='text-xs text-gray-400'>{r.legal_right_types__label || '—'}</div>
        </span>
      )
    },
    {
      title: 'Basis',
      key: 'basis',
      width: '24%',
      render: (_: unknown, r: LegalRight) => (
        <span>
          {r.legal_rights__basis === 'absolute' && <Tag>Absolute</Tag>}
          {r.legal_rights__basis === 'rule' && <Tag color='purple'>Rule</Tag>}
          {r.legal_rights__basis === 'right_reference' && <Tag color='blue'>References</Tag>}
          {ruleText(r) && (
            <div className='text-xs text-gray-500 tabular-nums mt-0.5'>{ruleText(r)}</div>
          )}
        </span>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: '18%',
      render: (_: unknown, r: LegalRight) => (
        <span>
          <RightStatusTag right={r} />
          {showCarriedNote && r.legal_rights__carried &&
            r.evaluation.status === 'in_force' &&
            r.evaluation.reason !== 'set_draft' && (
              <div className='text-xs text-gray-400 mt-0.5'>carried — survives supersession</div>
            )}
        </span>
      )
    },
    {
      title: 'Source',
      key: 'source',
      width: '24%',
      render: (_: unknown, r: LegalRight) => <SourceCell right={r} />
    },
    {
      title: 'Carried',
      key: 'carried',
      width: '8%',
      render: (_: unknown, r: LegalRight) =>
        r.legal_rights__carried ? <Tag color='gold'>Carried</Tag> : <span className='text-gray-400'>—</span>
    }
  ]

  return (
    <div ref={activateRef} className='px-2 py-4 space-y-4'>
      {/* Position strip — titleless shell by owner decision; the New Agreement
          Set action lives in the Agreement Sets section header below */}
      <FormSection>
        <div className='flex items-center gap-6 flex-wrap'>
          <span>
            <div className='text-xs text-gray-500'>Our % of FD (Equity)</div>
            <div className='text-lg font-medium tabular-nums'>{pct(position.our_pct_fd)}</div>
          </span>
          <span>
            <div className='text-xs text-gray-500'>Our % of Outstanding</div>
            <div className='text-lg font-medium tabular-nums'>{pct(position.our_pct_issued)}</div>
          </span>
          <span>
            <div className='text-xs text-gray-500'>Our Units (FD-Common Equiv., Today)</div>
            <div className='text-lg font-medium tabular-nums'>{shares(position.our_units)}</div>
          </span>
          <span className='text-xs text-gray-500'>
            {position.cap_table_versions__id ? (
              <>
                Evaluated against cap table{' '}
                <Tag color={position.cap_table_versions__status === 'current' ? 'green' : 'default'}>
                  {position.cap_table_versions__status}
                </Tag>
                as of {dayjs(position.as_of_date).format('MM/DD/YYYY')}
              </>
            ) : (
              'No reviewed cap-table version yet — rule-based rights evaluate as Unknown.'
            )}
          </span>
        </div>
        {position.stale_splits.length > 0 && (
          <Alert
            type='warning'
            showIcon
            className='mt-3'
            message={`The evaluated cap-table version pre-dates a booked share split (${position.stale_splits
              .map(
                (s) =>
                  `×${s.asset_transactions__split_factor ?? '?'} on ${dayjs(
                    s.asset_transactions__transaction_date
                  ).format('MM/DD/YYYY')}`
              )
              .join(
                ', '
              )}) — ownership percentages are inflated until the cap table is restated on the company's Cap Table tab.`}
          />
        )}
      </FormSection>

      {/* Rights in force (owner layout: summary first, sets below) */}
      <FormSection title='Rights In Force' titleColor='green'>
        {inForceRows.length === 0 ? (
          <div className='text-sm text-gray-400 py-2'>
            No rights are currently in force
            {data.sets.length === 0
              ? ' — capture the first executed agreement set with "New Agreement Set".'
              : '.'}
          </div>
        ) : (
          <Table
            size='small'
            rowKey={(row) => row.right.legal_rights__id}
            pagination={false}
            dataSource={inForceRows}
            columns={[
              {
                title: 'Right',
                key: 'right',
                width: '26%',
                render: (_: unknown, row) => (
                  <span>
                    <span className='font-medium'>{row.right.legal_rights__name}</span>
                    <div className='text-xs text-gray-400'>
                      {row.right.legal_right_types__label || '—'}
                    </div>
                  </span>
                )
              },
              {
                title: 'From',
                key: 'from',
                width: '26%',
                render: (_: unknown, row) => (
                  <span className='text-xs'>
                    {row.set.legal_agreement_sets__name}
                    {row.set.rounds__name && (
                      <Tag className='ml-1'>{row.set.rounds__name}</Tag>
                    )}
                    {row.set.legal_agreement_sets__status === 'superseded' && (
                      <div className='text-gray-400 mt-0.5'>carried from a superseded set</div>
                    )}
                  </span>
                )
              },
              {
                title: 'Basis',
                key: 'basis',
                width: '24%',
                render: (_: unknown, row) => (
                  <span>
                    {row.right.legal_rights__basis === 'absolute' && <Tag>Absolute</Tag>}
                    {row.right.legal_rights__basis === 'rule' && <Tag color='purple'>Rule</Tag>}
                    {row.right.legal_rights__basis === 'right_reference' && (
                      <Tag color='blue'>References</Tag>
                    )}
                    {ruleText(row.right) && (
                      <div className='text-xs text-gray-500 tabular-nums mt-0.5'>
                        {ruleText(row.right)}
                      </div>
                    )}
                  </span>
                )
              },
              {
                title: 'Status',
                key: 'status',
                render: (_: unknown, row) => <RightStatusTag right={row.right} />
              }
            ]}
          />
        )}
      </FormSection>

      {/* Agreement sets — collapsed by default, expandable per set or all at once */}
      <FormSection
        title='Agreement Sets'
        titleColor='purple'
        headerActions={
          <>
            {data.sets.length > 0 && (
              <Button
                size='small'
                onClick={() =>
                  setExpandedSets(
                    allExpanded ? [] : data.sets.map((s) => s.legal_agreement_sets__id)
                  )
                }
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            )}
            <Button size='small' type='primary' onClick={openNewSet}>
              New Agreement Set
            </Button>
          </>
        }
      >
        {data.sets.length === 0 ? (
          <div className='text-sm text-gray-400 py-2'>
            No agreement sets yet — capture the first executed set with &quot;New Agreement
            Set&quot;.
          </div>
        ) : (
          <Collapse
            size='small'
            activeKey={expandedSets}
            onChange={(keys) => setExpandedSets(Array.isArray(keys) ? keys : [keys])}
            items={data.sets.map((s) => ({
              key: s.legal_agreement_sets__id,
              label: (
                <span className='flex items-center gap-2 flex-wrap font-normal'>
                  <span className='font-semibold'>{s.legal_agreement_sets__name}</span>
                  <Tag color={SET_STATUS_COLORS[s.legal_agreement_sets__status] || 'default'}>
                    {s.legal_agreement_sets__status}
                  </Tag>
                  {s.rounds__name && <Tag>Round: {s.rounds__name}</Tag>}
                  {s.deals__name && <Tag>Deal: {s.deals__name}</Tag>}
                  <span className='text-xs text-gray-500'>
                    {s.legal_agreement_sets__executed_date
                      ? `executed ${dayjs(s.legal_agreement_sets__executed_date).format('MM/DD/YYYY')}`
                      : ''}
                    {s.legal_agreement_sets__status === 'superseded' && s.superseded_by_name
                      ? ` · superseded by ${s.superseded_by_name}${
                          s.legal_agreement_sets__superseded_date
                            ? ` on ${dayjs(s.legal_agreement_sets__superseded_date).format('MM/DD/YYYY')}`
                            : ''
                        }`
                      : ''}
                    {` · ${s.rights.length} right${s.rights.length === 1 ? '' : 's'}`}
                  </span>
                </span>
              ),
              extra:
                s.legal_agreement_sets__status === 'superseded' ? (
                  <span className='text-xs text-gray-400'>immutable history</span>
                ) : (
                  // Actions stay usable from the collapsed header; clicks must
                  // not toggle the panel.
                  <span
                    className='flex items-center gap-2'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button size='small' onClick={() => openEditSet(s)}>
                      Edit
                    </Button>
                    {s.legal_agreement_sets__status === 'draft' && (
                      <>
                        <Button size='small' type='primary' onClick={() => openExecute(s)}>
                          Execute…
                        </Button>
                        <Popconfirm
                          title='Delete this agreement set?'
                          description='The set and its rights are soft-deleted (recoverable in the database).'
                          onConfirm={() => runDelete(s)}
                        >
                          <Button size='small' danger>
                            Delete
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                    {s.legal_agreement_sets__status === 'executed' && (
                      <Popconfirm
                        title='Revert to draft?'
                        description='Any sets this one superseded return to executed.'
                        onConfirm={() => runRevert(s)}
                      >
                        <Button size='small'>Revert to Draft</Button>
                      </Popconfirm>
                    )}
                  </span>
                ),
              children:
                s.rights.length === 0 ? (
                  <div className='text-sm text-gray-400 py-2'>
                    No rights captured in this set yet.
                  </div>
                ) : (
                  <Table
                    size='small'
                    rowKey='legal_rights__id'
                    pagination={false}
                    dataSource={s.rights}
                    columns={rightColumns(s.legal_agreement_sets__status === 'superseded')}
                  />
                )
            }))}
          />
        )}
      </FormSection>

      {/* Editor */}
      {editorOpen && (
        <LegalSetEditorModal
          open={editorOpen}
          accountId={accountId}
          initial={editorInitial}
          rightTypes={data.right_types}
          ruleVariables={data.rule_variables}
          companyRounds={data.company_rounds}
          companyDeals={data.company_deals}
          companyDocuments={data.company_documents}
          referenceOptions={referenceOptions}
          onRefreshRounds={refetch}
          onClose={() => setEditorOpen(false)}
          onSaved={refetch}
        />
      )}

      {/* Execute modal */}
      <Modal
        title={executeSet ? `Execute Agreement Set — ${executeSet.legal_agreement_sets__name}` : ''}
        open={!!executeSet}
        onCancel={() => setExecuteSet(null)}
        okText='Execute'
        okButtonProps={{ loading: executeSaving }}
        onOk={runExecute}
        width={560}
      >
        <div className='space-y-3 py-2'>
          <div className='grid grid-cols-[130px_1fr] gap-2 items-center'>
            <span className='text-xs text-gray-500'>Executed Date</span>
            <DatePicker
              size='small'
              className='w-44'
              format='MM/DD/YYYY'
              value={executeDate ? dayjs(executeDate) : null}
              onChange={(d) => setExecuteDate(d ? d.format('YYYY-MM-DD') : null)}
            />
          </div>
          <div>
            <div className='text-xs text-gray-500 mb-1'>
              Supersedes these executed sets (their rights lapse on the executed date):
            </div>
            {data.sets.filter((x) => x.legal_agreement_sets__status === 'executed').length === 0 ? (
              <div className='text-xs text-gray-400'>No executed sets to supersede.</div>
            ) : (
              <div className='border border-gray-200 rounded p-2 space-y-1'>
                {data.sets
                  .filter((x) => x.legal_agreement_sets__status === 'executed')
                  .map((x) => (
                    <div key={x.legal_agreement_sets__id}>
                      <Checkbox
                        checked={executeSupersedes.includes(x.legal_agreement_sets__id)}
                        onChange={(e) =>
                          setExecuteSupersedes((prev) =>
                            e.target.checked
                              ? [...prev, x.legal_agreement_sets__id]
                              : prev.filter((id) => id !== x.legal_agreement_sets__id)
                          )
                        }
                      >
                        <span className='text-sm'>
                          {x.legal_agreement_sets__name}
                          <span className='text-xs text-gray-500'>
                            {x.legal_agreement_sets__executed_date
                              ? ` — executed ${dayjs(x.legal_agreement_sets__executed_date).format('MM/DD/YYYY')}`
                              : ''}{' '}
                            · {x.rights.length} right{x.rights.length === 1 ? '' : 's'}
                            {x.rights.some((r) => r.legal_rights__carried)
                              ? `, ${x.rights.filter((r) => r.legal_rights__carried).length} carried`
                              : ''}
                          </span>
                        </span>
                      </Checkbox>
                    </div>
                  ))}
              </div>
            )}
            <div className='text-xs text-gray-400 mt-1'>
              Carried rights (side letters) survive and stay in force. Nothing is deleted —
              superseded sets remain as history.
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}
